import { z } from "zod";

export interface ProductVariant {
  name: string;
  price?: string;
  imageUrl?: string;
  description?: string;
}

export const productVariantSchema = z.object({
  name: z.string().default(""),
  price: z.string().optional(),
  imageUrl: z.string().optional(),
  description: z.string().optional(),
});

function asStr(v: unknown): string {
  return typeof v === "string" ? v : "";
}

export function normalizeProductVariants(raw: unknown): ProductVariant[] {
  if (!Array.isArray(raw)) return [];
  const out: ProductVariant[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const rec = r as Record<string, unknown>;
    const variant: ProductVariant = { name: asStr(rec.name) };
    const price = asStr(rec.price).trim();
    if (price) variant.price = price;
    const imageUrl = asStr(rec.imageUrl).trim();
    if (imageUrl) variant.imageUrl = imageUrl;
    const description = asStr(rec.description).trim();
    if (description) variant.description = description;
    out.push(variant);
  }
  return out;
}

export function findProductVariant(
  product: unknown,
  identifier: string | null | undefined,
): ProductVariant | null {
  const wanted = asStr(identifier).trim();
  if (!wanted || !product || typeof product !== "object") return null;
  const variants = normalizeProductVariants((product as Record<string, unknown>).variantsJson);
  return variants.find((v) => v.name.trim() === wanted) ?? null;
}

function prependUnique(image: string, arr: unknown): string[] {
  const existing = Array.isArray(arr)
    ? (arr as unknown[]).filter((u): u is string => typeof u === "string" && u.length > 0)
    : [];
  return [image, ...existing.filter((u) => u !== image)];
}

export function applyVariantToProduct<T>(
  product: T,
  variant: ProductVariant | null | undefined,
): T {
  if (!product || typeof product !== "object" || !variant) return product;
  const src = product as Record<string, unknown>;
  const next: Record<string, unknown> = { ...src };

  const img = asStr(variant.imageUrl).trim();
  if (img) {
    next.adgenImageRefs = prependUnique(img, src.adgenImageRefs);
    next.mediaJson = prependUnique(img, src.mediaJson);
  }

  const desc = asStr(variant.description).trim();
  if (desc) next.description = desc;

  return next as T;
}
